// WipByDisciplineStrip.js
// -----------------------------------------------------------------------------
// Renders the WIP breakdown by job category (AV / LTG / SHD / IT / HT) with
// each category's Contract, WIP to Complete, and % Complete. Pulls from
// /api/projects/wip/by-discipline (live off `projects`, no snapshotting).
//
// Answers Jonathan Flamm's July 2026 ask for per-discipline WIP visibility on
// the Command Center Executive Summary / WIP page. Ties out to Pat Kross's
// WIP_to_Complete_for_CC.xlsx dollar-for-dollar on Contract, Estimate, Billed.
//
// Pat/Tom 2026-07-16: "Job Inventory on Hand" is now its own tracked
// category per job and is rolled into the WIP-to-Complete calculation
// (Total Cost to Date = JTD Cost + Inventory on Hand). See route comments
// in server/routes/projects.js for the full math. Until sync-sage.js is
// populating sage_inventory_on_hand, this value comes through as $0 and
// the tile behaves exactly as before.
//
// Chris G. 2026-07-20: Section title renamed "WIP by Discipline" -> "WIP by
// Job Category". Totals summary line moved to the LEFT of the header row and
// expanded to show Total Contract, Total Cost Estimate, Total Billed to Date,
// and WIP Total Cost to Complete on one line. "Window Trmts" tile relabeled
// to "Shades". Tooltip labels updated to Chris's wording.
//
// Pat Kross 2026-07-16 (revised summary, wired 2026-07-21): reframed the
// completion metric to "% to be Completed" (WIP-to-Complete / Total Estimate)
// and the dollar figure to "Balance to Be Completed" (Total Estimate − Total
// Cost to Date). Same math as before, aligned to Pat's revised terminology so
// Command Center and her spreadsheet read the same.
//
// Chris G. 2026-07-21: dollar label renamed "Balance to Be Completed" ->
// "WIP - Total Cost to Complete" (per Chris's follow-up on Pat's revised
// summary). "% to be Completed" wording unchanged.
//
// Chris G. 2026-07-21 (2nd pass): top summary bar rearranged + expanded to
// exactly Chris's ordering, with two new derived metrics:
//   Total Contract · Total Cost Estimate · WIP - Total Cost to Complete (%) ·
//   Est GP · Total Billed to Date (%) · WIP - Yet to be Billed (%)
//   Est GP % = (Contract − Total Cost Estimate) / Contract
//   WIP - Yet to be Billed % = wip_in_house / Contract  (= 100 − pct_billed)
// Both derived client-side from existing /api/projects/wip/by-discipline
// fields — no backend change.
// -----------------------------------------------------------------------------

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Tooltip,
  Chip,
  LinearProgress,
} from '@mui/material';
import api from '../services/api';

const DISCIPLINE_LABEL = {
  AV:  { title: 'AV',            color: '#0D2C54' },
  LTG: { title: 'Lighting',      color: '#4A6FA5' },
  SHD: { title: 'Shades',        color: '#7A9BC7' },
  IT:  { title: 'IT',            color: '#5C8AAF' },
  HT:  { title: 'Home Theater',  color: '#A85A4A' },
};

const fmtM = (n) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtFull = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const DisciplineCard = ({ d }) => {
  const label = DISCIPLINE_LABEL[d.discipline] || { title: d.discipline, color: '#8898A4' };
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
          <div><strong>{label.title}</strong> — {d.jobs} jobs</div>
          <div>Total Contract: {fmtFull(d.contract)}</div>
          <div>Total Cost Estimate (Equip and Labor): {fmtFull(d.estimate)}</div>
          <div>Total JTD Costs (Equip and Labor): {fmtFull(d.jtd_cost)}</div>
          <div>Job Inventory on Hand: {fmtFull(d.inventory_on_hand)}</div>
          <div><em>Total Cost to Date</em> (JTD + Inv): {fmtFull(d.total_cost_to_date)}</div>
          <div>Total Work Billed to Date: {fmtFull(d.billed)} ({d.pct_billed}%)</div>
          <div style={{ marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 4 }}>
            <strong>WIP yet to be Billed</strong> (contract − billed): {fmtFull(d.wip_in_house)}
          </div>
          <div><strong>WIP - Total Cost to Complete</strong> (Total Estimate − Total Cost to Date): {fmtFull(d.wip_to_complete)}</div>
          <div><strong>% to be Completed</strong>: {d.pct_to_complete}%</div>
        </Box>
      }
    >
      <Card
        variant="outlined"
        sx={{
          flex: '1 1 0',
          minWidth: 150,
          borderLeft: `4px solid ${label.color}`,
          cursor: 'help',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: label.color, letterSpacing: 0.5 }}>
              {label.title.toUpperCase()}
            </Typography>
            <Chip
              size="small"
              label={`${d.jobs}`}
              sx={{ height: 18, fontSize: '0.65rem', bgcolor: `${label.color}15`, color: label.color, fontWeight: 700 }}
            />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0D2C54', lineHeight: 1.1 }}>
            {fmtM(d.wip_to_complete)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#8898A4' }}>
            WIP - Total Cost to Complete
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, d.pct_complete))}
              sx={{ height: 4, borderRadius: 2, bgcolor: '#EEF1F5', '& .MuiLinearProgress-bar': { bgcolor: label.color } }}
            />
            <Typography variant="caption" sx={{ color: '#8898A4', fontSize: '0.65rem' }}>
              {d.pct_to_complete}% to be completed · {fmtM(d.contract)} contract
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Tooltip>
  );
};

// Filters accepted by the strip. Kept in lockstep with the WIP page's own
// filter set (Projects.js) so the strip totals track the header "Sage WIP
// Total" chip when the user narrows by PM, Sales, Client, Engagement, etc.
// (Tom Clancy 2026-07-31 — Pat Kross reconciliation follow-up.)
// `discipline` is intentionally excluded: the strip is already the per-
// discipline breakdown, and the chip is hidden by Projects.js when that
// filter is active.
const WipByDisciplineStrip = ({ compact = false, showTitle = true, filters = null }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Serialize just the filter keys the endpoint understands so the effect
  // dep list stays stable.
  const pm_id         = filters?.pm_id         || '';
  const sales_id      = filters?.sales_id      || '';
  const health_status = filters?.health_status || '';
  const ar_code       = filters?.ar_code       || '';
  const engagement_id = filters?.engagement_id || '';
  const search        = filters?.search        || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (pm_id)         params.pm_id = pm_id;
        if (sales_id)      params.sales_id = sales_id;
        if (health_status) params.health_status = health_status;
        if (ar_code)       params.ar_code = ar_code;
        if (engagement_id) params.engagement_id = engagement_id;
        if (search)        params.search = search;
        const res = await api.get('/projects/wip/by-discipline', { params });
        if (!cancelled) { setData(res.data); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pm_id, sales_id, health_status, ar_code, engagement_id, search]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        {[1, 2, 3, 4, 5].map((k) => (
          <Skeleton key={k} variant="rounded" height={110} sx={{ flex: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" sx={{ color: 'error.main', mb: 2, display: 'block' }}>
        WIP breakdown unavailable: {error}
      </Typography>
    );
  }

  if (!data || !data.disciplines?.length) return null;
  const { disciplines, totals } = data;

  // Derived metrics for Chris G.'s expanded summary bar (2026-07-21 pass 2).
  // Est GP % = (Contract − Total Cost Estimate) / Contract
  // % Yet to be Billed = wip_in_house / Contract  (= 100 − pct_billed)
  const estGpPct = totals.contract > 0
    ? Math.round(((totals.contract - totals.estimate) / totals.contract) * 1000) / 10
    : 0;
  const pctYetToBill = totals.contract > 0
    ? Math.round((totals.wip_in_house / totals.contract) * 1000) / 10
    : 0;

  return (
    <Box sx={{ mb: 2 }}>
      {showTitle && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          {/* Totals summary row — moved to the left per Chris G. 2026-07-20. */}
          {/* One line: Total Contract, Total Cost Estimate, Total Billed to Date, WIP Total Cost to Complete. */}
          <Tooltip
            arrow
            title={
              <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                <div><strong>{totals.jobs} active jobs</strong></div>
                <div>Total Contract: {fmtFull(totals.contract)}</div>
                <div>Total Cost Estimate (Equip and Labor): {fmtFull(totals.estimate)}</div>
                <div>Total JTD Costs (Equip and Labor): {fmtFull(totals.jtd_cost)}</div>
                <div>Job Inventory on Hand: {fmtFull(totals.inventory_on_hand)}</div>
                <div><em>Total Cost to Date</em>: {fmtFull(totals.total_cost_to_date)}</div>
                <div>Total Work Billed to Date: {fmtFull(totals.billed)} ({totals.pct_billed}%)</div>
                <div style={{ marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 4 }}>
                  <strong>WIP - Total Cost to Complete</strong> (Total Estimate − Total Cost to Date): {fmtFull(totals.wip_to_complete)} ({totals.pct_to_complete}%)
                </div>
                <div><strong>Est GP</strong> (Contract − Total Cost Estimate) / Contract: {estGpPct}%</div>
                <div><strong>WIP - Yet to be Billed</strong> (Contract − Billed): {fmtFull(totals.wip_in_house)} ({pctYetToBill}%)</div>
                <div style={{ marginTop: 4 }}>Live from Sage. Reflects sage_status = "In progress". WIP-to-Complete now nets Inventory on Hand per Pat/Tom 2026-07-16. Summary bar layout / added Est GP + WIP Yet to be Billed per Chris G. 2026-07-21 (2nd pass).</div>
              </Box>
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 2,
                rowGap: 0.5,
                cursor: 'help',
                color: '#0D2C54',
                fontSize: '0.78rem',
                fontWeight: 600,
              }}
            >
              <Box component="span">
                <Box component="span" sx={{ color: '#5B6B7D', fontWeight: 500, mr: 0.5 }}>Total Contract:</Box>
                {fmtM(totals.contract)}
              </Box>
              <Box component="span" sx={{ color: '#C3CBD3' }}>·</Box>
              <Box component="span">
                <Box component="span" sx={{ color: '#5B6B7D', fontWeight: 500, mr: 0.5 }}>Total Cost Estimate:</Box>
                {fmtM(totals.estimate)}
              </Box>
              <Box component="span" sx={{ color: '#C3CBD3' }}>·</Box>
              <Box component="span" sx={{ color: '#0D2C54', fontWeight: 700 }}>
                <Box component="span" sx={{ color: '#5B6B7D', fontWeight: 500, mr: 0.5 }}>WIP - Total Cost to Complete:</Box>
                {fmtM(totals.wip_to_complete)} ({totals.pct_to_complete}%)
              </Box>
              <Box component="span" sx={{ color: '#C3CBD3' }}>·</Box>
              <Box component="span">
                <Box component="span" sx={{ color: '#5B6B7D', fontWeight: 500, mr: 0.5 }}>Est GP:</Box>
                {estGpPct}%
              </Box>
              <Box component="span" sx={{ color: '#C3CBD3' }}>·</Box>
              <Box component="span">
                <Box component="span" sx={{ color: '#5B6B7D', fontWeight: 500, mr: 0.5 }}>Total Billed to Date:</Box>
                {fmtM(totals.billed)} ({totals.pct_billed}%)
              </Box>
              <Box component="span" sx={{ color: '#C3CBD3' }}>·</Box>
              <Box component="span">
                <Box component="span" sx={{ color: '#5B6B7D', fontWeight: 500, mr: 0.5 }}>WIP - Yet to be Billed:</Box>
                {fmtM(totals.wip_in_house)} ({pctYetToBill}%)
              </Box>
            </Box>
          </Tooltip>
          <Typography
            variant="subtitle2"
            sx={{ color: '#0D2C54', fontWeight: 700, letterSpacing: 0.3, ml: 'auto' }}
          >
            WIP by Job Category
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {disciplines.map((d) => <DisciplineCard key={d.discipline} d={d} />)}
      </Box>
    </Box>
  );
};

export default WipByDisciplineStrip;
